if (!customElements.get('cart-recommendations')) {
  customElements.define(
    'cart-recommendations',
    class CartRecommendations extends HTMLElement {
      static requests = new Map();

      constructor() {
        super();
        this.addEventListener('click', this.onClick.bind(this));
      }

      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.querySelectorAll('[data-cart-rec-panel]').forEach((panel) => this.initPanel(panel));
        if (this.querySelector('.cart-recs__loading')) this.loadRecommendations();
      }

      async loadRecommendations() {
        const productId = this.dataset.productId;
        if (!productId) return;

        const limit = parseInt(this.dataset.limit, 10) || 4;
        const requestKey = `${productId}:${limit}`;

        if (!CartRecommendations.requests.has(requestKey)) {
          CartRecommendations.requests.set(
            requestKey,
            fetch(`${window.Shopify.routes.root}recommendations/products.json?product_id=${productId}&limit=${limit}`)
              .then((response) => {
                if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);
                return response.json();
              })
              .then((data) => data.products || []),
          );
        }

        try {
          const products = await CartRecommendations.requests.get(requestKey);
          this.renderRecommendations(products.slice(0, limit));
        } catch (error) {
          console.error(error);
          this.closest('.cart-recs')?.setAttribute('hidden', '');
        }
      }

      renderRecommendations(products) {
        const list = this.querySelector('[data-cart-rec-list]');
        if (!list) return;

        if (!products.length) {
          this.closest('.cart-recs')?.setAttribute('hidden', '');
          return;
        }

        list.innerHTML = products.map((product) => this.createProductMarkup(product)).join('');
        list.querySelectorAll('[data-cart-rec-panel]').forEach((panel) => this.initPanel(panel));
      }

      createProductMarkup(product) {
        const variants = product.variants || [];
        const firstVariant = variants.find((variant) => variant.available) || variants[0];
        if (!firstVariant) return '';

        const image = product.featured_image || product.images?.[0] || '';
        const compareAtPrice = Number(firstVariant.compare_at_price || 0);
        const price = Number(firstVariant.price || product.price || 0);
        const hasSale = compareAtPrice > price;
        const optionNames = product.options || [];
        let sizeOptionIndex = optionNames.findIndex((option) => /talla|size|tama(?:ñ|n)o/i.test(option));
        if (sizeOptionIndex < 0) {
          sizeOptionIndex = optionNames.findIndex((option) => !/colou?r|tono/i.test(option));
        }
        if (sizeOptionIndex < 0) sizeOptionIndex = 0;
        const variantsMarkup = variants.length > 1
          ? `<div class="card-variant-panel-wrapper cart-recs__variant-panel-wrapper color-scheme-1"><div class="card-variant-panel" data-variant-panel data-cart-rec-panel>${variants
              .map((variant) => {
                const rawLabel = variant.options?.[sizeOptionIndex] || variant.title || '';
                const numericParts = String(rawLabel).match(/\d+(?:[.,]\d+)?/g);
                const label = numericParts?.join(' / ') || rawLabel;
                return `<button type="button" class="card-variant-panel__option${variant.available ? '' : ' is-unavailable'}" data-variant-option data-cart-rec-variant data-variant-id="${variant.id}"${variant.available ? '' : ' disabled'}><span data-variant-option-text>${this.escapeHTML(label)}</span><div class="loading__spinner hidden"><svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66"><circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"></circle></svg></div></button>`;
              })
              .join('')}</div></div>`
          : `<button type="button" class="quick-add__submit button button--full-width" data-cart-rec-add data-variant-id="${firstVariant.id}"${firstVariant.available ? '' : ' disabled'}>${this.escapeHTML(firstVariant.available ? this.dataset.labelAdd : this.dataset.labelSoldOut)}</button>`;

        return `<li class="cart-recs__item">
          <a href="${this.escapeHTML(product.url)}" class="cart-recs__item-media" tabindex="-1" aria-hidden="true">
            ${image ? `<img src="${this.escapeHTML(image)}" alt="${this.escapeHTML(product.title)}" loading="lazy" width="160" height="160">` : ''}
          </a>
          <div class="cart-recs__item-info">
            <a href="${this.escapeHTML(product.url)}" class="cart-recs__item-title">${this.escapeHTML(product.title)}</a>
            <div class="cart-recs__item-price price">
              <span class="price-item ${hasSale ? 'price-item--sale' : 'price-item--regular'}">${this.formatMoney(price)}</span>
              ${hasSale ? `<s class="price-item price-item--regular">${this.formatMoney(compareAtPrice)}</s>` : ''}
            </div>
          </div>
          <div class="cart-recs__item-actions">${variantsMarkup}</div>
        </li>`;
      }

      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '${{amount}}';
        const value = cents / 100;
        const group = (number, thousands, decimals) => {
          const fixed = number.toFixed(decimals ? 2 : 0);
          const [whole, decimal] = fixed.split('.');
          const groupedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
          return decimal ? `${groupedWhole}${decimals}${decimal}` : groupedWhole;
        };

        return format
          .replace('{{amount_no_decimals_with_comma_separator}}', group(value, '.', ''))
          .replace('{{amount_with_comma_separator}}', group(value, '.', ','))
          .replace('{{amount_no_decimals}}', group(value, ',', ''))
          .replace('{{amount}}', group(value, ',', '.'));
      }

      escapeHTML(value) {
        return String(value || '')
          .replaceAll('&', '&amp;')
          .replaceAll('<', '&lt;')
          .replaceAll('>', '&gt;')
          .replaceAll('"', '&quot;')
          .replaceAll("'", '&#039;');
      }

      /*
        La barra de scroll del selector de tallas sólo se muestra cuando hay
        más chips de los que caben. Se marca con .is-scrollable en vez de
        dejar overflow-x: scroll fijo (que pinta la barra siempre).
      */
      initPanel(panel) {
        if (panel.dataset.panelInit) return;
        panel.dataset.panelInit = 'true';
        this.initDragScroll(panel);
        this.initOverflowWatch(panel);
      }

      initOverflowWatch(panel) {
        const update = () => panel.classList.toggle('is-scrollable', panel.scrollWidth - panel.clientWidth > 1);
        update();
        if ('ResizeObserver' in window) {
          const observer = new ResizeObserver(update);
          observer.observe(panel);
          Array.from(panel.children).forEach((child) => observer.observe(child));
        } else {
          window.addEventListener('resize', update);
        }
        if (document.fonts?.ready) document.fonts.ready.then(update);
      }

      initDragScroll(panel) {
        let isDragging = false;
        let startX = 0;
        let scrollLeft = 0;

        panel.addEventListener('pointerdown', (event) => {
          isDragging = true;
          startX = event.clientX;
          scrollLeft = panel.scrollLeft;
          panel.setAttribute('data-dragging', '');
        });

        panel.addEventListener('pointermove', (event) => {
          if (!isDragging) return;
          const delta = event.clientX - startX;
          if (Math.abs(delta) > 3) panel.dataset.dragged = 'true';
          panel.scrollLeft = scrollLeft - delta;
        });

        const stopDrag = () => {
          isDragging = false;
          panel.removeAttribute('data-dragging');
        };
        panel.addEventListener('pointerup', stopDrag);
        panel.addEventListener('pointerleave', stopDrag);
      }

      onClick(event) {
        const panel = event.target.closest('[data-cart-rec-panel]');
        if (panel && panel.dataset.dragged === 'true') {
          delete panel.dataset.dragged;
          return;
        }

        const button = event.target.closest('[data-cart-rec-variant], [data-cart-rec-add]');
        if (!button || button.disabled) return;

        this.addToCart(button);
      }

      addToCart(button) {
        const variantId = button.dataset.variantId;
        if (!variantId) return;

        const cartDrawer = document.querySelector('cart-drawer');
        const spinner = button.querySelector('.loading__spinner');
        const panel = button.closest('[data-cart-rec-panel]');
        button.disabled = true;
        button.classList.add('loading');
        panel?.setAttribute('data-loading', '');
        spinner?.classList.remove('hidden');

        const sections =
          cartDrawer && typeof cartDrawer.getSectionsToRender === 'function'
            ? cartDrawer.getSectionsToRender().map((section) => section.section || section.id)
            : ['cart-drawer', 'cart-icon-bubble'];

        fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1, sections }),
        })
          .then((response) => response.json())
          .then((data) => {
            if (data.status) {
              throw new Error(data.description || data.message);
            }
            if (cartDrawer) {
              cartDrawer.classList.remove('is-empty');
              cartDrawer.renderContents(data);
            }
          })
          .catch((error) => console.error(error))
          .finally(() => {
            button.disabled = false;
            button.classList.remove('loading');
            panel?.removeAttribute('data-loading');
            spinner?.classList.add('hidden');
          });
      }
    }
  );
}
